'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';

export function RegisterPage() {
  const router = useRouter();
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!email.trim()) {
      setError('Имэйл шаардлагатай');
      return;
    }

    if (password.length < 8) {
      setError('Нууц үг хамгийн багадаа 8 тэмдэгт байх ёстой');
      return;
    }

    if (password !== confirmPassword) {
      setError('Нууц үг таарахгүй байна');
      return;
    }

    setLoading(true);
    try {
      await api.auth.register({
        email: email.trim(),
        password,
      });
      // Redirect to check-email page
      router.push(`/check-email?email=${encodeURIComponent(email.trim())}`);
    } catch (err: any) {
      const message = err.message || 'Бүртгэл амжилтгүй боллоо';
      setError(message);
      addToast({
        title: 'Алдаа',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl">Бүртгэл үүсгэх</CardTitle>
            <CardDescription>
              Бүртгүүлэхийн тулд мэдээллээ оруулна уу
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Имэйл
                </label>
                <Input
                  id="email"
                  data-testid="register-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-2">
                  Нууц үг
                </label>
                <Input
                  id="password"
                  data-testid="register-password"
                  type="password"
                  placeholder="Хамгийн багадаа 8 тэмдэгт"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">Хамгийн багадаа 8 тэмдэгт байх ёстой</p>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                  Нууц үг баталгаажуулах
                </label>
                <Input
                  id="confirmPassword"
                  data-testid="register-confirm-password"
                  type="password"
                  placeholder="Нууц үгээ давтан оруулна уу"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <Button
                type="submit"
                data-testid="register-submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Бүртгэл үүсгэж байна...' : 'Бүртгэл үүсгэх'}
              </Button>
              <div className="text-center text-sm">
                <span className="text-gray-600">Бүртгэл аль хэдийн байна уу? </span>
                <Link href="/login" className="text-blue-600 hover:underline">
                  Нэвтрэх
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

